/**
 * Command Palette
 * Spotlight-style command interface
 * Access via ⌘K or ?
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  FileText,
  Users,
  Briefcase,
  DollarSign,
  Settings,
  User,
  Plus,
  Search,
  Clock,
  Calendar,
  Mail,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  HelpCircle,
  Keyboard,
  LogOut,
} from 'lucide-react';
import { useCommandPalette, useLawityContext, useMatterState } from '../../context/LawityContext';
import { backdropVariants, modalVariants } from '../../design-system/animations';

interface CommandItem {
  id: string;
  label: string;
  shortcut?: string;
  icon: React.ReactNode;
  action: () => void;
  group: string;
  keywords?: string[];
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { isOpen, close } = useCommandPalette();
  const { matter } = useMatterState();
  const { setHelpOpen, isAudioPlaying, setAudioPlaying, isZenMode, setZenMode } = useLawityContext();
  const [search, setSearch] = useState('');

  const handleSelect = useCallback((action: () => void) => {
    action();
    close();
    setSearch('');
  }, [close]);

  const commands = useMemo<CommandItem[]>(() => [
    // Navigation
    {
      id: 'nav-home',
      label: 'Go to Dashboard',
      shortcut: 'G H',
      icon: <Home className="w-4 h-4" />,
      action: () => navigate('/'),
      group: 'Navigation',
      keywords: ['home', 'dashboard', 'main'],
    },
    {
      id: 'nav-matters',
      label: 'Go to Matters',
      shortcut: 'G M',
      icon: <Briefcase className="w-4 h-4" />,
      action: () => navigate('/matters'),
      group: 'Navigation',
      keywords: ['matters', 'cases', 'projects'],
    },
    {
      id: 'nav-clients',
      label: 'Go to Clients',
      shortcut: 'G C',
      icon: <Users className="w-4 h-4" />,
      action: () => navigate('/clients'),
      group: 'Navigation',
      keywords: ['clients', 'contacts', 'people'],
    },
    {
      id: 'nav-documents',
      label: 'Go to Documents',
      shortcut: 'G D',
      icon: <FileText className="w-4 h-4" />,
      action: () => navigate('/documents'),
      group: 'Navigation',
      keywords: ['documents', 'files', 'docs'],
    },
    {
      id: 'nav-billing',
      label: 'Go to Billing',
      shortcut: 'G B',
      icon: <DollarSign className="w-4 h-4" />,
      action: () => navigate('/billing'),
      group: 'Navigation',
      keywords: ['billing', 'invoices', 'payments', 'money'],
    },
    {
      id: 'nav-settings',
      label: 'Go to Settings',
      shortcut: 'G S',
      icon: <Settings className="w-4 h-4" />,
      action: () => navigate('/settings'),
      group: 'Navigation',
      keywords: ['settings', 'preferences', 'config'],
    },
    {
      id: 'nav-profile',
      label: 'Go to Profile',
      shortcut: 'G P',
      icon: <User className="w-4 h-4" />,
      action: () => navigate('/profile'),
      group: 'Navigation',
      keywords: ['profile', 'account', 'me'],
    },

    // Actions
    {
      id: 'action-new-matter',
      label: 'Create New Matter',
      shortcut: 'N',
      icon: <Plus className="w-4 h-4" />,
      action: () => navigate('/matters/new'),
      group: 'Actions',
      keywords: ['new', 'create', 'matter', 'case'],
    },
    {
      id: 'action-new-client',
      label: 'Add New Client',
      icon: <Plus className="w-4 h-4" />,
      action: () => navigate('/clients/new'),
      group: 'Actions',
      keywords: ['new', 'add', 'client', 'contact'],
    },
    {
      id: 'action-new-document',
      label: 'Create Document',
      icon: <FileText className="w-4 h-4" />,
      action: () => navigate('/documents/new'),
      group: 'Actions',
      keywords: ['new', 'create', 'document', 'draft'],
    },
    {
      id: 'action-search',
      label: 'Global Search',
      shortcut: '/',
      icon: <Search className="w-4 h-4" />,
      action: () => {
        // Open dedicated search
        navigate('/search');
      },
      group: 'Actions',
      keywords: ['search', 'find', 'lookup'],
    },

    // Quick Access (if matter is active)
    ...(matter.id ? [
      {
        id: 'quick-matter-timeline',
        label: `View ${matter.name} Timeline`,
        icon: <Clock className="w-4 h-4" />,
        action: () => navigate(`/matters/${matter.id}/timeline`),
        group: 'Current Matter',
        keywords: ['timeline', 'history', 'events'],
      },
      {
        id: 'quick-matter-calendar',
        label: `${matter.name} Calendar`,
        icon: <Calendar className="w-4 h-4" />,
        action: () => navigate(`/matters/${matter.id}/calendar`),
        group: 'Current Matter',
        keywords: ['calendar', 'schedule', 'deadlines'],
      },
      {
        id: 'quick-matter-messages',
        label: `${matter.name} Messages`,
        icon: <Mail className="w-4 h-4" />,
        action: () => navigate(`/matters/${matter.id}/messages`),
        group: 'Current Matter',
        keywords: ['messages', 'email', 'communication'],
      },
    ] : []),

    // Audio Controls
    {
      id: 'audio-toggle',
      label: isAudioPlaying ? 'Pause Briefing' : 'Play Briefing',
      shortcut: 'P',
      icon: isAudioPlaying ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />,
      action: () => setAudioPlaying(!isAudioPlaying),
      group: 'Audio',
      keywords: ['audio', 'briefing', 'play', 'pause'],
    },
    {
      id: 'audio-zen',
      label: isZenMode ? 'Exit Zen Mode' : 'Enter Zen Mode',
      shortcut: 'Z',
      icon: isZenMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />,
      action: () => setZenMode(!isZenMode),
      group: 'Audio',
      keywords: ['zen', 'focus', 'distraction'],
    },

    // Help
    {
      id: 'help-shortcuts',
      label: 'Keyboard Shortcuts',
      shortcut: '?',
      icon: <Keyboard className="w-4 h-4" />,
      action: () => setHelpOpen(true),
      group: 'Help',
      keywords: ['shortcuts', 'keyboard', 'help', 'keys'],
    },
    {
      id: 'help-docs',
      label: 'Documentation',
      icon: <HelpCircle className="w-4 h-4" />,
      action: () => window.open('/docs', '_blank'),
      group: 'Help',
      keywords: ['docs', 'documentation', 'help', 'guide'],
    },

    // Account
    {
      id: 'account-logout',
      label: 'Sign Out',
      icon: <LogOut className="w-4 h-4" />,
      action: () => navigate('/logout'),
      group: 'Account',
      keywords: ['logout', 'signout', 'exit'],
    },
  ], [navigate, matter, isAudioPlaying, setAudioPlaying, isZenMode, setZenMode, setHelpOpen]);

  // Group commands
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {};
    commands.forEach((cmd) => {
      if (!groups[cmd.group]) groups[cmd.group] = [];
      groups[cmd.group].push(cmd);
    });
    return groups;
  }, [commands]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={close}
          />

          {/* Command Dialog */}
          <motion.div
            className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-xl z-50"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Command
              className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              loop
            >
              {/* Search Input */}
              <div className="flex items-center border-b border-neutral-200 dark:border-neutral-700 px-4">
                <Search className="w-5 h-5 text-neutral-400 mr-3" />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Type a command or search..."
                  className="w-full py-4 bg-transparent text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none text-base"
                />
                <kbd className="hidden sm:inline-flex px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-500 rounded">
                  ESC
                </kbd>
              </div>

              {/* Command List */}
              <Command.List className="max-h-[400px] overflow-y-auto p-2">
                <Command.Empty className="py-6 text-center text-neutral-500">
                  No results found.
                </Command.Empty>

                {Object.entries(groupedCommands).map(([group, items]) => (
                  <Command.Group
                    key={group}
                    heading={group}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
                  >
                    {items.map((item) => (
                      <Command.Item
                        key={item.id}
                        value={`${item.label} ${item.keywords?.join(' ') || ''}`}
                        onSelect={() => handleSelect(item.action)}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 aria-selected:bg-neutral-100 dark:aria-selected:bg-neutral-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-neutral-400">{item.icon}</span>
                          <span>{item.label}</span>
                        </div>
                        {item.shortcut && (
                          <kbd className="text-xs text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded">
                            {item.shortcut}
                          </kbd>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
              </Command.List>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-xs text-neutral-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">↑↓</kbd>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">↵</kbd>
                    select
                  </span>
                </div>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">esc</kbd>
                  close
                </span>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default CommandPalette;
